import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, Home, Sparkles, ShieldCheck, AlertTriangle } from "lucide-react";

const ALLOWED = [
  "Hotels, lodges, guest houses, boutique apartments — anywhere legally licensed to host paying guests in Zambia",
  "Clear, honest titles that describe the property (\"Sunset Lodge — Livingstone\", not \"BEST PLACE EVER!!\")",
  "At least 3 high-resolution photos taken in daylight; interior, exterior, and one representative room",
  "A specific address so guests know where to arrive and physical-key handoffs work",
  "Kwacha prices for both hourly (from 2 hours) and nightly stays, matching the property class",
  "Amenity list that matches reality — Wi-Fi, parking, breakfast, air-conditioning only if actually provided",
];

const REJECTED = [
  "Duplicate listings — the same property submitted more than once (list one entry with your full inventory)",
  "Fake or stock photos not showing the actual property",
  "Adult-entertainment venues or unlicensed short-let party spaces",
  "Properties whose declared amenities materially don't match on-site reality (guests will report and refunds get issued)",
  "Prices priced far above market rate with no clear justification (guarding against bait-and-switch)",
  "Contact details in the description trying to redirect guests off-platform to avoid EasyStay's payment protection",
  "Listings that impersonate other hosts, chains, or well-known brands",
];

const TIPS = [
  { icon: Sparkles, title: "Complete KYC first", body: "Verified hosts auto-approve. Unverified submissions wait for admin review, which can take up to 24 hours." },
  { icon: ShieldCheck, title: "Sharp, honest photos", body: "Well-lit photos taken with a modern phone in landscape mode convert 3× better than dim night shots." },
  { icon: CheckCircle2, title: "Set fair hourly pricing", body: "Hourly rate should be roughly 1/8 of nightly. Under-price and you'll get walk-in traffic that pays your electricity bill." },
];

export default function HostGuidelines() {
  const updated = "August 2026";
  return (
    <div className="mx-auto max-w-4xl px-6 py-12 lg:px-8" data-testid="host-guidelines-page">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-copper">
        <Home className="h-3.5 w-3.5" /> Host content policy
      </div>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
        Listing content guidelines
      </h1>
      <p className="mt-2 text-sm text-slate-500">EasyStay Zambia · Last updated {updated}</p>
      <p className="mt-6 max-w-2xl text-slate-700">
        Verified hosts auto-approve, and unverified submissions land in an admin queue. Follow these rules so
        your listing gets approved on the first try — and stays approved.
      </p>

      <section className="mt-10 rounded-3xl bg-white p-8 card-shadow">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-6 w-6 text-green-600" />
          <h2 className="font-display text-xl font-bold text-slate-900">What we approve</h2>
        </div>
        <ul className="mt-4 space-y-2">
          {ALLOWED.map((line) => (
            <li key={line} className="flex items-start gap-2 text-sm text-slate-700">
              <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-green-500" />
              {line}
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-8 rounded-3xl bg-white p-8 card-shadow">
        <div className="flex items-center gap-2">
          <XCircle className="h-6 w-6 text-rose-600" />
          <h2 className="font-display text-xl font-bold text-slate-900">What we reject</h2>
        </div>
        <ul className="mt-4 space-y-2">
          {REJECTED.map((line) => (
            <li key={line} className="flex items-start gap-2 text-sm text-slate-700">
              <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-rose-500" />
              {line}
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-8 rounded-3xl bg-forest p-8 text-white">
        <h2 className="font-display text-xl font-bold">Tips that speed up approval</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {TIPS.map((t) => (
            <div key={t.title} className="rounded-2xl bg-white/10 p-4 backdrop-blur">
              <t.icon className="h-5 w-5 text-copper" />
              <div className="mt-2 text-sm font-bold">{t.title}</div>
              <p className="mt-1 text-xs text-white/80">{t.body}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-8 flex items-start gap-3 rounded-2xl bg-amber-50 p-5 text-sm text-amber-900">
        <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
        <div>
          If your listing is rejected we&apos;ll email a specific reason so you can update and resubmit. Listings that
          violate these guidelines twice may be suspended.
        </div>
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">Questions? <a href="mailto:hosts@easystayzm.com" className="text-copper hover:underline">hosts@easystayzm.com</a></p>
        <Link to="/host/new" data-testid="host-guidelines-cta" className="btn-primary text-sm">
          Start your listing
        </Link>
      </div>
    </div>
  );
}
